import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

// Diff estilo GitHub (unificado): linhas removidas em vermelho, adicionadas em
// verde, palavras que mudaram dentro da linha realçadas, e só CONTEXT linhas de
// contexto em volta de cada trecho alterado — o resto vira "⋯ N linhas iguais"
// (clicável para expandir). Sem dependências: LCS sobre linhas e, dentro de
// cada par removida/adicionada, LCS sobre palavras.
const CONTEXT = 3;

// LCS genérico → lista de operações [" "|"-"|"+", item]. Corta prefixo/sufixo
// comuns antes (caso típico: edição pontual num texto grande fica barata).
const diffSeq = (a, b, eq = (x, y) => x === y) => {
  let pre = 0;
  while (pre < a.length && pre < b.length && eq(a[pre], b[pre])) pre++;
  let suf = 0;
  while (suf < a.length - pre && suf < b.length - pre && eq(a[a.length - 1 - suf], b[b.length - 1 - suf])) suf++;
  const A = a.slice(pre, a.length - suf);
  const B = b.slice(pre, b.length - suf);
  const mid = [];
  if (A.length * B.length > 4000000) {
    // grande demais p/ LCS: tudo removido + tudo adicionado
    A.forEach(x => mid.push(["-", x]));
    B.forEach(x => mid.push(["+", x]));
  } else {
    const n = A.length;
    const m = B.length;
    const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
    for (let i = n - 1; i >= 0; i--)
      for (let j = m - 1; j >= 0; j--)
        dp[i][j] = eq(A[i], B[j]) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    let i = 0;
    let j = 0;
    while (i < n || j < m) {
      if (i < n && j < m && eq(A[i], B[j])) { mid.push([" ", A[i]]); i++; j++; }
      else if (j < m && (i === n || dp[i][j + 1] >= dp[i + 1][j])) { mid.push(["+", B[j]]); j++; }
      else { mid.push(["-", A[i]]); i++; }
    }
    // remoções antes das adições dentro de cada bloco alterado (como o GitHub)
    for (let k = 0; k < mid.length; ) {
      if (mid[k][0] === " ") { k++; continue; }
      let e = k;
      while (e < mid.length && mid[e][0] !== " ") e++;
      const blk = mid.slice(k, e);
      mid.splice(k, e - k, ...blk.filter(x => x[0] === "-"), ...blk.filter(x => x[0] === "+"));
      k = e;
    }
  }
  return [...a.slice(0, pre).map(x => [" ", x]), ...mid, ...a.slice(a.length - suf).map(x => [" ", x])];
};

// texto sem espaços no fim das linhas nem linhas em branco no fim
export const normWs = s => String(s || "").replace(/[ \t]+$/gm, "").trimEnd();

const tokens = s => s.split(/(\s+|[.,;:!?()[\]{}"'/\\-])/).filter(t => t !== "");

// realce de palavras dentro de um par removida/adicionada
const wordDiff = (oldLine, newLine) => {
  const ops = diffSeq(tokens(oldLine), tokens(newLine));
  const del = [];
  const add = [];
  ops.forEach(([t, w]) => {
    if (t !== "+") del.push([t === "-", w]);
    if (t !== "-") add.push([t === "+", w]);
  });
  return { del, add };
};

// linhas finais com numeração e realce intra-linha
export const buildRows = (before, after) => {
  const a = before === "" ? [] : String(before).split("\n");
  const b = after === "" ? [] : String(after).split("\n");
  const ops = diffSeq(a, b);
  const rows = [];
  let ln = 1;
  let rn = 1;
  for (let k = 0; k < ops.length; ) {
    if (ops[k][0] === " ") { rows.push({ t: " ", l: ln++, r: rn++, text: ops[k][1] }); k++; continue; }
    const dels = [];
    const adds = [];
    while (k < ops.length && ops[k][0] === "-") dels.push(ops[k++][1]);
    while (k < ops.length && ops[k][0] === "+") adds.push(ops[k++][1]);
    const pairs = Math.min(dels.length, adds.length);
    const parts = dels.map((d, i) => (i < pairs ? wordDiff(d, adds[i]) : null));
    for (let i = 0; i < dels.length; i++) rows.push({ t: "-", l: ln++, text: dels[i], words: parts[i] && parts[i].del });
    for (let i = 0; i < adds.length; i++) rows.push({ t: "+", r: rn++, text: adds[i], words: i < pairs ? parts[i].add : null });
  }
  return rows;
};

// agrupa em trechos: alterações + CONTEXT linhas em volta; o resto vira "gap"
const hunks = rows => {
  const keep = new Array(rows.length).fill(false);
  rows.forEach((r, i) => {
    if (r.t === " ") return;
    for (let k = Math.max(0, i - CONTEXT); k <= Math.min(rows.length - 1, i + CONTEXT); k++) keep[k] = true;
  });
  const out = [];
  for (let i = 0; i < rows.length; ) {
    if (keep[i]) { out.push({ row: rows[i], i }); i++; continue; }
    const start = i;
    while (i < rows.length && !keep[i]) i++;
    out.push({ gap: rows.slice(start, i), id: start });
  }
  return out;
};

const useStyles = makeStyles(theme => {
  const a = theme.app || {};
  return {
    box: { border: `1px solid ${a.border || "#E6E9ED"}`, borderRadius: 8, overflow: "hidden", background: "#fff", fontFamily: a.mono || "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5, lineHeight: "20px" },
    scroll: { maxHeight: 460, overflow: "auto" },
    row: { display: "flex", minWidth: 0 },
    num: { width: 40, flexShrink: 0, textAlign: "right", padding: "0 8px", color: "#9AA3AD", userSelect: "none", fontSize: 11.5 },
    sign: { width: 18, flexShrink: 0, textAlign: "center", userSelect: "none", color: "#9AA3AD" },
    text: { flex: 1, minWidth: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", padding: "0 10px 0 2px", color: "#24292f" },
    del: { background: "#FFEBE9", "& $num": { background: "#FFD7D5", color: "#82071E" }, "& $sign": { color: "#CF222E" } },
    add: { background: "#E6FFEC", "& $num": { background: "#CCFFD8", color: "#116329" }, "& $sign": { color: "#1A7F37" } },
    wDel: { background: "#FFB3AE", borderRadius: 2 },
    wAdd: { background: "#ABF2BC", borderRadius: 2 },
    ctx: { "& $text": { color: "#6E7781" } }, // contexto (não mudou) em cinza p/ não parecer adição
    blank: { fontStyle: "italic", color: "#9AA3AD" },
    legend: { display: "flex", alignItems: "center", gap: 14, padding: "6px 12px", borderBottom: `1px solid ${a.border || "#E6E9ED"}`, background: "#FAFBFC", fontFamily: a.font || "inherit", fontSize: 11.5, color: "#57606A" },
    sw: { display: "inline-block", width: 10, height: 10, borderRadius: 2, marginRight: 5, verticalAlign: -1 },
    note: { padding: "8px 12px", background: "#FFF8E1", color: "#7A5A00", fontSize: 12.5, borderBottom: "1px solid #F1E2B3", fontFamily: a.font || "inherit" },
    gap: { background: "#F1F8FF", color: "#57606A", fontSize: 12, padding: "2px 10px 2px 66px", cursor: "pointer", userSelect: "none", fontFamily: "inherit", "&:hover": { background: "#DDF4FF", color: "#0969DA" } },
    empty: { padding: "8px 12px", color: "#9AA3AD", fontStyle: "italic" },
  };
});

const DiffView = ({ before = "", after = "" }) => {
  const classes = useStyles();
  const [open, setOpen] = useState({}); // gaps expandidos
  const parts = useMemo(() => hunks(buildRows(before || "", after || "")), [before, after]);

  const line = (r, key) => (
    <div key={key} className={clsx(classes.row, r.t === "-" && classes.del, r.t === "+" && classes.add, r.t === " " && classes.ctx)}>
      <span className={classes.num}>{r.l || ""}</span>
      <span className={classes.num}>{r.r || ""}</span>
      <span className={classes.sign}>{r.t === " " ? "" : r.t}</span>
      <span className={classes.text}>
        {r.words
          ? r.words.map(([chg, w], i) => (chg ? <span key={i} className={r.t === "-" ? classes.wDel : classes.wAdd}>{w}</span> : w))
          : r.text.trim() === "" && r.t !== " "
            ? <span className={classes.blank}>(linha em branco)</span>
            : r.text || " "}
      </span>
    </div>
  );

  if (!parts.length) return <div className={classes.box}><div className={classes.empty}>(vazio)</div></div>;

  const wsOnly = normWs(before) === normWs(after);

  return (
    <div className={classes.box}>
      <div className={classes.legend}>
        <span><span className={classes.sw} style={{ background: "#FFD7D5" }} />removido</span>
        <span><span className={classes.sw} style={{ background: "#CCFFD8" }} />adicionado</span>
        <span>sem cor = não mudou (contexto)</span>
      </div>
      {wsOnly && <div className={classes.note}>Só muda espaço ou linha em branco — nenhuma mudança no conteúdo.</div>}
      <div className={classes.scroll}>
        {parts.map(p =>
          p.gap ? (
            open[p.id] ? (
              p.gap.map((r, i) => line(r, `g${p.id}-${i}`))
            ) : (
              <div key={`gap${p.id}`} className={classes.gap} onClick={() => setOpen(o => ({ ...o, [p.id]: true }))}>
                ⋯ {p.gap.length} {p.gap.length === 1 ? "linha igual" : "linhas iguais"} (mostrar)
              </div>
            )
          ) : (
            line(p.row, p.i)
          )
        )}
      </div>
    </div>
  );
};

export default DiffView;
